import MarkdownIt from 'markdown-it'
import markdownItMathjax from './markdown-it-mathjax'
import { renderMath } from './mathjax-renderer'
import markdownItTaskLists from 'markdown-it-task-lists'
import markdownItContainer from 'markdown-it-container'
import markdownItFootnote from 'markdown-it-footnote'
import markdownItMark from 'markdown-it-mark'
import markdownItSub from 'markdown-it-sub'
import markdownItSup from 'markdown-it-sup'
import markdownItAbbr from 'markdown-it-abbr'
import markdownItDeflist from 'markdown-it-deflist'
import { full as markdownItEmoji } from 'markdown-it-emoji'
import { createHighlighter, type Highlighter } from 'shiki'
import { $typst, TypstSnippet } from '@myriaddreamin/typst.ts/dist/esm/contrib/snippet.mjs'
import { createCodeBlockId } from './code-block-actions'

const TYSTP_FONT_CDN = 'https://cdn.jsdmirror.cn/gh/typst/typst-assets@v0.13.1/files/fonts/'
const TYSTP_COMPILER_WASM =
  'https://cdn.jsdmirror.cn/npm/@myriaddreamin/typst-ts-web-compiler@0.7.0/pkg/typst_ts_web_compiler_bg.wasm'
let typstConfigured = false

const CODE_BLOCK_ICONS = {
  chevron:
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>',
  copy: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>',
  download:
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>',
}

interface PendingTypst {
  id: string
  content: string
  ppi: number
}

export interface MarkdownRendererOptions {
  highlightTheme?: string | string[]
  typstOptions?: TypstCompileOptions
  enableTypst?: boolean
  /** @deprecated use enableMath */
  enableKatex?: boolean
  enableMath?: boolean
  enableShiki?: boolean
}

export interface TypstCompileOptions {
  format?: 'svg' | 'png' | 'pdf'
  ppi?: number
  dpi?: number
  embedFont?: boolean
  excludes?: string[]
}

class MarkdownRenderer {
  private md: MarkdownIt
  private highlighter: Highlighter | null = null
  private options: Required<MarkdownRendererOptions>
  private initialized = false
  private pendingTypst: PendingTypst[] = []
  private typstSlotSeq = 0

  constructor(options: MarkdownRendererOptions = {}) {
    this.options = {
      highlightTheme: options.highlightTheme ?? ['one-light', 'github-dark'],
      typstOptions: options.typstOptions ?? {},
      enableTypst: options.enableTypst ?? true,
      enableMath: options.enableMath ?? options.enableKatex ?? true,
      enableKatex: options.enableKatex ?? options.enableMath ?? true,
      enableShiki: options.enableShiki ?? true,
    }

    this.md = this.createMarkdownIt()
  }

  private createMarkdownIt(): MarkdownIt {
    const md = MarkdownIt({
      html: true,
      linkify: true,
      typographer: true,
      breaks: true,
    })

    md.use(markdownItMathjax)
    md.use(markdownItTaskLists, { enabled: false, label: true, labelAfter: false })
    md.use(markdownItFootnote)
    md.use(markdownItMark)
    md.use(markdownItSub)
    md.use(markdownItSup)
    md.use(markdownItAbbr)
    md.use(markdownItDeflist)
    md.use(markdownItEmoji)

    md.use(markdownItContainer, 'tip', {
      validate: (params: string) => params.trim().match(/^tip\s*(.*)$/),
      render: (tokens: any[], idx: number) => {
        const m = tokens[idx].info.trim().match(/^tip\s*(.*)$/)
        if (tokens[idx].nesting === 1) {
          return `<div class="markdown-container tip"><div class="markdown-container-title">${m?.[1] || 'Tip'}</div>\n`
        }
        return '</div>\n'
      },
    })

    md.use(markdownItContainer, 'warning', {
      validate: (params: string) => params.trim().match(/^warning\s*(.*)$/),
      render: (tokens: any[], idx: number) => {
        const m = tokens[idx].info.trim().match(/^warning\s*(.*)$/)
        if (tokens[idx].nesting === 1) {
          return `<div class="markdown-container warning"><div class="markdown-container-title">${m?.[1] || 'Warning'}</div>\n`
        }
        return '</div>\n'
      },
    })

    md.use(markdownItContainer, 'danger', {
      validate: (params: string) => params.trim().match(/^danger\s*(.*)$/),
      render: (tokens: any[], idx: number) => {
        const m = tokens[idx].info.trim().match(/^danger\s*(.*)$/)
        if (tokens[idx].nesting === 1) {
          return `<div class="markdown-container danger"><div class="markdown-container-title">${m?.[1] || 'Danger'}</div>\n`
        }
        return '</div>\n'
      },
    })

    md.use(markdownItContainer, 'info', {
      validate: (params: string) => params.trim().match(/^info\s*(.*)$/),
      render: (tokens: any[], idx: number) => {
        const m = tokens[idx].info.trim().match(/^info\s*(.*)$/)
        if (tokens[idx].nesting === 1) {
          return `<div class="markdown-container info"><div class="markdown-container-title">${m?.[1] || 'Info'}</div>\n`
        }
        return '</div>\n'
      },
    })

    md.use(markdownItContainer, 'details', {
      validate: (params: string) => params.trim().match(/^details\s*(.*)$/),
      render: (tokens: any[], idx: number) => {
        const m = tokens[idx].info.trim().match(/^details\s*(.*)$/)
        if (tokens[idx].nesting === 1) {
          return `<details class="markdown-details"><summary>${m?.[1] || 'Details'}</summary>\n`
        }
        return '</details>\n'
      },
    })

    const defaultFence = md.renderer.rules.fence!
    md.renderer.rules.fence = (tokens, idx, options, env, self) => {
      const token = tokens[idx]
      const lang = token.info.trim().split(/\s+/)[0]

      if (this.options.enableTypst && lang === 'typst') {
        return this.emitTypstPlaceholder(token.content, token.info)
      }

      if (this.options.enableMath && (lang === 'math' || lang === 'latex' || lang === 'katex')) {
        const parts = token.info.trim().split(/\s+/)
        return this.renderMath(token.content, !parts.includes('inline'))
      }

      if (this.options.enableShiki && lang) {
        return this.renderShiki(token.content, lang)
      }

      const fallback = defaultFence(tokens, idx, options, env, self)
      if (lang) {
        return this.wrapCodeBlock(lang, token.content, fallback)
      }
      return fallback
    }

    return md
  }

  async initialize(): Promise<void> {
    if (this.initialized) return

    if (this.options.enableTypst) {
      try {
        if (!typstConfigured) {
          $typst.setCompilerInitOptions({
            getModule: () => Promise.resolve(TYSTP_COMPILER_WASM),
          })
          $typst.setRendererInitOptions({
            getModule: () =>
              import('@myriaddreamin/typst-ts-renderer/pkg/typst_ts_renderer_bg.wasm?url').then(
                (m) => m.default,
              ),
          })
          $typst.use(
            TypstSnippet.preloadFontAssets({
              assets: ['text'],
              assetUrlPrefix: TYSTP_FONT_CDN,
            }),
          )
          typstConfigured = true
        }
      } catch (error) {
        console.warn('Typst init failed, will be retried at render time:', error)
      }
    }

    if (this.options.enableShiki) {
      const themes = Array.isArray(this.options.highlightTheme)
        ? this.options.highlightTheme
        : [this.options.highlightTheme]

      this.highlighter = await createHighlighter({
        themes,
        langs: [
          'javascript', 'typescript', 'python', 'rust', 'go', 'java', 'cpp', 'c', 'csharp',
          'html', 'css', 'scss', 'json', 'yaml', 'toml', 'markdown', 'bash', 'shell',
          'sql', 'dockerfile', 'nginx', 'vue', 'jsx', 'tsx', 'svelte', 'astro',
          'ruby', 'php', 'swift', 'kotlin', 'scala', 'clojure', 'haskell', 'elm',
          'r', 'julia', 'matlab', 'perl', 'lua', 'dart', 'zig', 'ocaml', 'fsharp',
        ],
      })
    }

    this.initialized = true
  }

  private emitTypstPlaceholder(content: string, info: string): string {
    const id = `typst-slot-${++this.typstSlotSeq}`
    const ppi = this.parseTypstOptions(info).ppi ?? 144
    this.pendingTypst.push({ id, content, ppi })
    return `<span class="typst-slot" data-id="${id}"></span>`
  }

private async compileTypst(content: string, ppi: number): Promise<string> {
    try {
      const svg = $typst.svg ? await $typst.svg({ mainContent: content }) : await failTypst()

      if (typeof svg === 'string' && svg.trim().startsWith('<')) {
        return `<div class="typst-block" style="text-align:center">${svg}</div>`
      }

      throw new Error(svg || 'Typst 编译未返回输出')
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      return `<pre class="typst-error"><code>${this.escapeHtml(msg)}</code></pre>`
    }
  }

  private parseTypstOptions(info: string): Partial<TypstCompileOptions> {
    const options: Partial<TypstCompileOptions> = {}
    const parts = info.trim().split(/\s+/).slice(1)

    for (const part of parts) {
      if (part.startsWith('format:')) {
        options.format = part.slice(7) as 'svg' | 'png' | 'pdf'
      } else if (part.startsWith('dpi:') || part.startsWith('ppi:')) {
        options.ppi = parseFloat(part.slice(4))
      } else if (part === 'embed-font') {
        options.embedFont = true
      }
    }

    return options
  }

  private renderMath(content: string, displayMode: boolean): string {
    const html = renderMath(content, displayMode)
    return displayMode ? `<div class="mathjax-block">${html}</div>` : html
  }

  private renderShiki(content: string, lang: string): string {
    if (!this.highlighter) {
      return this.wrapCodeBlock(lang, content, `<pre><code class="language-${lang}">${this.escapeHtml(content)}</code></pre>`)
    }

    try {
      const themes = Array.isArray(this.options.highlightTheme)
        ? this.options.highlightTheme
        : [this.options.highlightTheme]

      const lightTheme = themes.find(t => !t.includes('dark')) || themes[0]
      const darkTheme = themes.find(t => t.includes('dark')) || themes[0]

      const normalized = content.endsWith('\n') ? content.slice(0, -1) : content

      const highlighted = this.highlighter.codeToHtml(normalized, {
        lang,
        themes: {
          light: lightTheme,
          dark: darkTheme,
        },
        defaultColor: false,
        cssVariablePrefix: '--shiki-',
      })

      return this.wrapCodeBlock(lang, content, highlighted)
    } catch {
      return this.wrapCodeBlock(lang, content, `<pre><code class="language-${lang}">${this.escapeHtml(content)}</code></pre>`)
    }
  }

  private wrapCodeBlock(lang: string, content: string, innerHtml: string): string {
    const displayLang = (lang || 'code').charAt(0).toUpperCase() + (lang || 'code').slice(1)
    const normalized = content.endsWith('\n') ? content.slice(0, -1) : content
    const lineCount = Math.max(1, normalized ? normalized.split('\n').length : 1)
    const tall = lineCount > 10
    const gutterLines = Array.from({ length: lineCount }, (_, i) => i + 1).join('\n')

    return [
      `<div class="code-block${tall ? ' code-block--tall code-block--collapsed' : ''}" data-block-id="${createCodeBlockId(lang, content)}" data-lang="${this.escapeHtml(lang || 'code')}" style="--cb-lines: ${lineCount}">`,
      `<div class="code-block__header" title="折叠/展开代码">`,
      `<span class="code-block__arrow" aria-hidden="true">${CODE_BLOCK_ICONS.chevron}</span>`,
      `<span class="code-block__lang">${this.escapeHtml(displayLang)}</span>`,
      `<span class="code-block__actions">`,
      `<button type="button" class="code-block__copy" title="复制代码">${CODE_BLOCK_ICONS.copy}</button>`,
      `<button type="button" class="code-block__download" title="下载代码">${CODE_BLOCK_ICONS.download}</button>`,
      `</span>`,
      `</div>`,
      `<div class="code-block__body">`,
      `<div class="code-block__gutter" aria-hidden="true"><pre>${gutterLines}</pre></div>`,
      `<div class="code-block__scroll">${innerHtml}</div>`,
      `</div>`,
      `</div>`,
    ].join('')
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  async render(markdown: string): Promise<string> {
    await this.initialize()
    const pending = this.pendingTypst
    this.pendingTypst = []
    let html = this.md.render(markdown)

    if (pending.length) {
      const replacements = await Promise.all(
        pending.map((p) => this.compileTypst(p.content, p.ppi)),
      )
      pending.forEach((p, i) => {
        html = html.replaceAll(
          `<span class="typst-slot" data-id="${p.id}"></span>`,
          replacements[i],
        )
      })
    }

    return html
  }

  renderSync(markdown: string): string {
    if (!this.initialized) {
      console.warn('MarkdownRenderer not initialized, call initialize() first for full features')
    }
    return this.md.render(markdown)
  }

  setHighlightTheme(theme: string | string[]): void {
    this.options.highlightTheme = theme
    this.initialized = false
    this.highlighter = null
  }

  setOptions(options: Partial<MarkdownRendererOptions>): void {
    Object.assign(this.options, options)
    this.initialized = false
    this.highlighter = null
    this.md = this.createMarkdownIt()
  }
}

let defaultRenderer: MarkdownRenderer | null = null

export async function createMarkdownRenderer(options?: MarkdownRendererOptions): Promise<MarkdownRenderer> {
  const renderer = new MarkdownRenderer(options)
  await renderer.initialize()
  return renderer
}

export function getDefaultRenderer(): MarkdownRenderer {
  if (!defaultRenderer) {
    defaultRenderer = new MarkdownRenderer()
  }
  return defaultRenderer
}

export async function renderMarkdown(markdown: string, options?: MarkdownRendererOptions): Promise<string> {
  const renderer = await createMarkdownRenderer(options)
  return renderer.render(markdown)
}

export { MarkdownRenderer }

function failTypst(): Promise<never> {
  return Promise.reject(new Error('typst.ts 不可用或未正确初始化'))
}